/**
 * App.tsx — orchestrator
 *
 * Owns the view-routing state (which view is showing) + overlay state
 * (settings drawer, tutorial, paywall, lookup). Each view component is
 * focused on its own concern; App wires them together and holds the
 * cross-cutting state.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Locale, ThemeMode, World, LaddersFile } from './types';
import { loadLadders } from './data';
import { applyTheme } from './theme';
import {
  getStoredLocale, setStoredLocale,
  getStoredTheme,
  hasSeenTutorial, markTutorialSeen,
  resetAllProgress,
} from './storage';
import {
  initEntitlement, purchaseFullUnlock, refreshEntitlement,
  hasFullUnlockLocal,
} from './entitlements';
import { WorldList } from './WorldList';
import { PlayView } from './PlayView';
import { StatsView } from './StatsView';
import { SettingsDrawer } from './SettingsDrawer';
import { TutorialOverlay } from './TutorialOverlay';
import { PaywallOverlay } from './PaywallOverlay';
import { LookupOverlay } from './LookupOverlay';
import { useConfirm, useAlert } from './ConfirmDialog';

type View =
  | { kind: 'worldList' }
  | { kind: 'play'; world: World; ladderIdx: number }
  | { kind: 'stats' };

const FREE_WORLD_IDS = new Set(['threshold', 'garden', 'kitchen']);

function detectInitialLocale(): Locale {
  const stored = getStoredLocale();
  if (stored) return stored;
  // No browser-language detection in this build — explicit-via-Settings is
  // intentional for predictability; default EN if nothing stored.
  return 'en';
}

export function App() {
  const confirmDialog = useConfirm();
  const alertDialog = useAlert();
  const [locale, setLocale] = useState<Locale>(detectInitialLocale);
  const [theme, setTheme] = useState<ThemeMode>(getStoredTheme);
  const [unlocked, setUnlockedState] = useState<boolean>(hasFullUnlockLocal);
  const [purchasing, setPurchasing] = useState(false);
  const [worlds, setWorlds] = useState<readonly World[]>([]);
  const [view, setView] = useState<View>({ kind: 'worldList' });

  // Overlay state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(!hasSeenTutorial());
  const [paywallWorld, setPaywallWorld] = useState<World | null>(null);
  const [lookupWord, setLookupWord] = useState<string | null>(null);

  // Apply theme on mount + whenever it changes
  useEffect(() => { applyTheme(theme); }, [theme]);

  // Re-query the platform's IAP rail on boot — catches cross-device
  // restores (Apple ID / Play account ownership) the user didn't
  // explicitly tap Restore for.
  useEffect(() => {
    initEntitlement((now) => setUnlockedState(now));
  }, []);

  // Load worlds when locale changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data: LaddersFile | null = await loadLadders(locale);
      if (cancelled) return;
      setWorlds(data?.worlds ?? []);
    })();
    return () => { cancelled = true; };
  }, [locale]);

  // ────────────────────────────────────────────────────────────────────────
  // Navigation handlers

  const openWorld = (world: World, ladderIdx: number) => {
    const isFree = FREE_WORLD_IDS.has(world.worldId);
    if (!isFree && !unlocked) {
      setPaywallWorld(world);
      return;
    }
    setView({ kind: 'play', world, ladderIdx });
  };

  const backToWorlds = () => setView({ kind: 'worldList' });
  const openStats = () => setView({ kind: 'stats' });

  const changeLocale = (next: Locale) => {
    if (next === locale) return;
    setStoredLocale(next);
    setLocale(next);
    setView({ kind: 'worldList' });
    setSettingsOpen(false);
  };

  const changeTheme = (next: ThemeMode) => {
    setTheme(next);
    // applyTheme runs via the effect above
  };

  const closeTutorial = () => {
    markTutorialSeen();
    setTutorialOpen(false);
  };

  const purchaseUnlock = async () => {
    if (purchasing) return;
    setPurchasing(true);
    const target = paywallWorld;
    const result = await purchaseFullUnlock();
    setPurchasing(false);
    if (result.outcome === 'success') {
      setUnlockedState(true);
      setPaywallWorld(null);
      if (target) setView({ kind: 'play', world: target, ladderIdx: 0 });
      return;
    }
    if (result.outcome === 'cancelled') {
      // User cancelled the native sheet — stay on paywall, no message.
      return;
    }
    if (result.outcome === 'unsupported') {
      await alertDialog({
        title: 'Purchase unavailable here',
        message:
          'In-app purchases are available in the iOS and Android apps. ' +
          'On the web build the unlock isn’t purchasable yet.',
      });
      return;
    }
    await alertDialog({
      title: 'Purchase failed',
      message: result.error
        ? `Something went wrong:\n\n${result.error}`
        : 'Something went wrong. Please try again, or contact support@acgapps.org if it keeps happening.',
    });
  };

  const restorePurchase = async () => {
    const owned = await refreshEntitlement();
    if (owned) {
      setUnlockedState(true);
      setPaywallWorld(null);
      await alertDialog({
        title: 'Purchase restored',
        message: 'All 10 worlds are now unlocked on this device.',
      });
    } else {
      await alertDialog({
        title: 'No prior purchase',
        message:
          'We didn’t find a previous purchase tied to this account. ' +
          'If you bought the unlock on a different device, make sure you’re signed in with the same Apple ID or Google account.',
      });
    }
  };

  const onResetAllProgress = async () => {
    const ok = await confirmDialog({
      title: 'Reset all progress?',
      message:
        'Clears every solved ladder, saved solution, in-progress attempt, and learned-word count.\n\n' +
        'This is a local-only reset; the dictionary cache is preserved.',
      confirmLabel: 'Reset everything',
      cancelLabel: 'Keep my progress',
      danger: true,
    });
    if (!ok) return;
    resetAllProgress();
    setSettingsOpen(false);
    setView({ kind: 'worldList' });
  };

  const settingsBundle = useMemo(() => ({
    open: settingsOpen,
    theme,
    locale,
    onClose: () => setSettingsOpen(false),
    onThemeChange: changeTheme,
    onLocaleChange: changeLocale,
    onOpenStats: () => { setSettingsOpen(false); openStats(); },
    onOpenTutorial: () => { setSettingsOpen(false); setTutorialOpen(true); },
    onRestorePurchase: restorePurchase,
    onResetProgress: onResetAllProgress,
  }), [settingsOpen, theme, locale]);

  return (
    <>
      {view.kind === 'worldList' && (
        <WorldList
          locale={locale}
          worlds={worlds}
          unlocked={unlocked}
          onOpenWorld={openWorld}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      )}

      {view.kind === 'play' && (
        // `key` keyed on (world, ladderIdx) so React tears down + remounts
        // PlayView when navigating between ladders. Cleaner than manual
        // state-reset gymnastics.
        <PlayView
          key={`${view.world.worldId}-${view.ladderIdx}`}
          locale={locale}
          world={view.world}
          ladderIdx={view.ladderIdx}
          onBackToWorlds={backToWorlds}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenLookup={(w) => setLookupWord(w)}
          onPickLadder={(idx) => setView({ kind: 'play', world: view.world, ladderIdx: idx })}
        />
      )}

      {view.kind === 'stats' && (
        <StatsView
          locale={locale}
          worlds={worlds}
          onBackToWorlds={backToWorlds}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      )}

      {settingsOpen && <SettingsDrawer {...settingsBundle} />}
      {tutorialOpen && <TutorialOverlay onClose={closeTutorial} />}
      {paywallWorld && (
        <PaywallOverlay
          purchasing={purchasing}
          onClose={() => setPaywallWorld(null)}
          onBuy={purchaseUnlock}
          onRestore={restorePurchase}
        />
      )}
      {lookupWord && (
        <LookupOverlay
          word={lookupWord}
          locale={locale}
          onClose={() => setLookupWord(null)}
        />
      )}
    </>
  );
}
